import React, { useState, useEffect, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { Tabs, Metric, Button, Modal, Selectbox } from './bpm';
import { twsAPI, categoriesAPI, assetEnrichmentAPI, assetsAPI } from '../services/api';
import api from '../services/api';
import { useToast } from './bpm';
import { useCanEdit } from '../hooks/useCanEdit';
import { getPageIcon } from '../utils/pageIcons';
import './ActifDetail.css';

const ActifDetail = ({ actif, onBack, onLogoClick = null }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  const [enrichFormData, setEnrichFormData] = useState({
    yfinanceSymbol: actif.yfinance_symbol || actif.yfinanceSymbol || '',
    coursCible: actif.cours_cible || actif.coursCible || '',
    categorie: actif.categorie || actif.category || ''
  });
  const [categories, setCategories] = useState(() => {
    // Charger depuis sessionStorage si disponible (actif_detail ou portefeuille)
    const getCatsFromStorage = (key, tsKey) => {
      try {
        const savedCats = sessionStorage.getItem(key);
        const savedTs = sessionStorage.getItem(tsKey);
        if (savedCats && savedTs) {
          const cats = JSON.parse(savedCats);
          const activeCats = (cats || []).filter(cat => cat.is_active);
          if (activeCats.length > 0) {
            return [...activeCats].sort((a, b) => {
              const nameA = (a.name || '').toLowerCase();
              const nameB = (b.name || '').toLowerCase();
              return nameA.localeCompare(nameB);
            });
          }
        }
      } catch (e) { /* ignorer */ }
      return null;
    };
    return getCatsFromStorage('actif_detail_categories', 'actif_detail_categories_timestamp')
      || getCatsFromStorage('portefeuille_categories', 'portefeuille_categories_timestamp')
      || [];
  });
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingEnrichment, setLoadingEnrichment] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState(null);
  const { showToast } = useToast();
  const { canEdit } = useCanEdit();

  // Ref pour éviter les appels multiples simultanés
  const loadingCategoriesRef = useRef(false);
  const loadingEnrichmentRef = useRef(false);
  
  // Précharger les catégories dès le montage (actif_detail puis portefeuille comme fallback)
  useEffect(() => {
    const readCache = (key, tsKey) => {
      try {
        const savedCats = sessionStorage.getItem(key);
        const savedTs = sessionStorage.getItem(tsKey);
        if (savedCats && savedTs) {
          const age = Date.now() - parseInt(savedTs, 10);
          const cats = JSON.parse(savedCats);
          const sorted = (cats || []).filter(cat => cat.is_active).sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
          return { list: sorted, age };
        }
      } catch (e) { /* ignorer */ }
      return null;
    };
    const fromActif = readCache('actif_detail_categories', 'actif_detail_categories_timestamp');
    const fromPortefeuille = readCache('portefeuille_categories', 'portefeuille_categories_timestamp');
    const cached = (fromActif?.list?.length ? fromActif : fromPortefeuille?.list?.length ? fromPortefeuille : null);
    if (cached?.list?.length) {
      if (categories.length === 0 || categories.length !== cached.list.length) {
        setCategories(cached.list);
      }
      const age = cached.age ?? 0;
      if (age >= 5 * 60 * 1000 && !loadingCategoriesRef.current) {
        loadingCategoriesRef.current = true;
        loadCategories().finally(() => { loadingCategoriesRef.current = false; });
      }
      return;
    }
    if (categories.length === 0 && !loadingCategoriesRef.current) {
      loadingCategoriesRef.current = true;
      loadCategories().finally(() => { loadingCategoriesRef.current = false; });
    }
  }, []); // Seulement au montage

  useEffect(() => {
    if (actif && actif.symbol) {
      loadMarketData();
      // Ne charger les données d'enrichissement que si pas déjà chargées
      if (!enrichmentData && !loadingEnrichmentRef.current) {
        loadingEnrichmentRef.current = true;
        loadEnrichmentData().finally(() => {
          loadingEnrichmentRef.current = false;
        });
      }
    }
  }, [actif]);

  // Précharger les données quand la modal s'ouvre (optimisé pour éviter les appels multiples)
  useEffect(() => {
    if (showEnrichModal) {
      // Vérifier d'abord le cache avant de faire des appels API
      const loadDataIfNeeded = async () => {
        const promises = [];
        
        // TOUJOURS vérifier le cache sessionStorage quand la modal s'ouvre (actif_detail puis portefeuille)
        let categoriesFromCache = null;
        let categoriesLoaded = false;
        let cacheAge = 0;
        
        const readCache = (key, tsKey) => {
          try {
            const savedCats = sessionStorage.getItem(key);
            const savedTs = sessionStorage.getItem(tsKey);
            if (savedCats && savedTs) {
              const age = Date.now() - parseInt(savedTs, 10);
              const cats = JSON.parse(savedCats);
              const sorted = (cats || []).filter(cat => cat.is_active).sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              });
              return { list: sorted, age };
            }
          } catch (e) { /* ignorer */ }
          return null;
        };
        const fromActif = readCache('actif_detail_categories', 'actif_detail_categories_timestamp');
        const fromPortefeuille = readCache('portefeuille_categories', 'portefeuille_categories_timestamp');
        const cached = (fromActif?.list?.length ? fromActif : fromPortefeuille?.list?.length ? fromPortefeuille : null);
        if (cached?.list?.length) {
          categoriesFromCache = cached.list;
          cacheAge = cached.age ?? 0;
          if (categories.length === 0 || categories.length !== categoriesFromCache.length) {
            setCategories(categoriesFromCache);
          }
          categoriesLoaded = true;
          if (cacheAge >= 5 * 60 * 1000 && !loadingCategoriesRef.current) {
            loadingCategoriesRef.current = true;
            promises.push(loadCategories().finally(() => { loadingCategoriesRef.current = false; }));
          }
        }
        
        // Si pas de catégories chargées (ni depuis le cache ni depuis le state), charger depuis l'API
        if (!categoriesLoaded && categories.length === 0 && !loadingCategoriesRef.current) {
          loadingCategoriesRef.current = true;
          promises.push(loadCategories().finally(() => {
            loadingCategoriesRef.current = false;
          }));
        }
        
        // Charger les données d'enrichissement seulement si pas déjà chargées
        if (!enrichmentData && actif?.symbol && !loadingEnrichmentRef.current) {
          loadingEnrichmentRef.current = true;
          promises.push(loadEnrichmentData().finally(() => {
            loadingEnrichmentRef.current = false;
          }));
        }
        
        // Exécuter en parallèle seulement si nécessaire
        if (promises.length > 0) {
          await Promise.allSettled(promises);
        }
      };
      
      loadDataIfNeeded();
    }
  }, [showEnrichModal, actif?.symbol]); // Retirer categories.length et enrichmentData des dépendances pour éviter les re-renders

  // Charger les données d'enrichissement existantes (optimisé pour éviter les appels multiples)
  const loadEnrichmentData = async () => {
    if (!actif || !actif.symbol) return;
    
    // Ne pas recharger si déjà en cours
    if (loadingEnrichmentRef.current) {
      return;
    }
    
    const symbol = actif.symbol;
    loadingEnrichmentRef.current = true;
    setLoadingEnrichment(true);
    try {
      // Charger uniquement les données d'enrichissement (les catégories sont déjà chargées ou en cache)
      const enrichmentResponse = await Promise.race([
        assetEnrichmentAPI.getBySymbol(symbol),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000)) // Timeout de 10s au lieu de 30s
      ]).catch(err => {
        // Si timeout ou erreur, retourner null
        if (err.message === 'Timeout' || err.code === 'ECONNABORTED') {
          console.warn(`⚠️ [ActifDetail] Timeout lors du chargement de l'enrichissement pour ${symbol}`);
          return null;
        }
        throw err;
      });
      
      // Traiter la réponse d'enrichissement
      if (enrichmentResponse?.data) {
        const enrichmentData = enrichmentResponse.data;
        setEnrichmentData(enrichmentData);
        
        // Récupérer les catégories depuis le state ou sessionStorage (actif_detail puis portefeuille)
        let currentCategories = categories;
        if (currentCategories.length === 0) {
          const readCats = (key) => {
            try {
              const savedCats = sessionStorage.getItem(key);
              if (savedCats) {
                const cats = JSON.parse(savedCats);
                return (cats || []).filter(cat => cat.is_active).sort((a, b) => {
                  const nameA = (a.name || '').toLowerCase();
                  const nameB = (b.name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                });
              }
            } catch (e) { /* ignorer */ }
            return [];
          };
          const fromActif = readCats('actif_detail_categories');
          const fromPortefeuille = readCats('portefeuille_categories');
          currentCategories = fromActif.length ? fromActif : fromPortefeuille;
          if (currentCategories.length > 0) {
            setCategories(currentCategories);
          }
        }
        
        // Trouver le nom de la catégorie
        let categorieName = '';
        if (enrichmentData.categorie_id && currentCategories.length > 0) {
          const category = currentCategories.find(c => c.id === enrichmentData.categorie_id);
          categorieName = category ? category.name : '';
        }
        
        // Mettre à jour le formulaire avec les données existantes
        setEnrichFormData({
          yfinanceSymbol: enrichmentData.yfinance_symbol || '',
          coursCible: enrichmentData.cours_cible || '',
          categorie: categorieName
        });
      }
    } catch (err) {
      // 404 est normal si aucune donnée d'enrichissement n'existe encore
      if (err.response?.status !== 404 && err.message !== 'Timeout') {
        console.error('Error loading enrichment data:', err);
      }
    } finally {
      setLoadingEnrichment(false);
      loadingEnrichmentRef.current = false;
    }
  };

  const loadCategories = async () => {
    // Ne pas recharger si déjà en cours (double protection)
    if (loadingCategories || loadingCategoriesRef.current) {
      // Attendre que le chargement en cours se termine
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!loadingCategories && !loadingCategoriesRef.current) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 50);
      });
    }
    
    // Charger depuis actif_detail_categories ou portefeuille_categories (fallback)
    const loadFromCache = (allowExpired = false) => {
      const tryKey = (key, tsKey) => {
        try {
          const savedCats = sessionStorage.getItem(key);
          const savedTs = sessionStorage.getItem(tsKey);
          if (savedCats && savedTs) {
            const age = Date.now() - parseInt(savedTs, 10);
            if (allowExpired || age < 5 * 60 * 1000) {
              const cats = JSON.parse(savedCats);
              const activeCats = (cats || []).filter(cat => cat.is_active).sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              });
              if (activeCats.length > 0) {
                setCategories(activeCats);
                return true;
              }
            }
          }
        } catch (e) { /* ignorer */ }
        return false;
      };
      return tryKey('actif_detail_categories', 'actif_detail_categories_timestamp')
        || tryKey('portefeuille_categories', 'portefeuille_categories_timestamp');
    };
    
    // Vérifier le cache avant de faire l'appel API
    if (loadFromCache(false)) {
      return; // Cache valide, retourner immédiatement
    }
    
    loadingCategoriesRef.current = true;
    setLoadingCategories(true);
    try {
      // Utiliser un timeout réduit de 10 secondes (comme pour l'enrichissement)
      // Utiliser api.get() directement pour avoir un contrôle sur le timeout
      const response = await api.get('/categories', { timeout: 10000 });
      const activeCategories = response.data.filter(cat => cat.is_active);
      // Trier les catégories par ordre alphabétique
      const sortedCategories = [...activeCategories].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setCategories(sortedCategories);
      
      // Mettre en cache dans sessionStorage pour un chargement instantané la prochaine fois
      try {
        sessionStorage.setItem('actif_detail_categories', JSON.stringify(response.data));
        sessionStorage.setItem('actif_detail_categories_timestamp', Date.now().toString());
      } catch (e) {
        // Ignorer les erreurs de sessionStorage
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      
      // En cas d'erreur (timeout, réseau, etc.), essayer d'utiliser le cache même expiré
      if (loadFromCache(true)) {
        // Cache utilisé (même expiré)
      } else {
        // Pas de cache disponible, afficher une erreur seulement si la modal est ouverte
        if (showEnrichModal) {
          showToast('Impossible de charger la liste des catégories. Veuillez réessayer.', 'error', 3000, 'Erreur de chargement', 'Portefeuille', getPageIcon('Portefeuille'));
        }
      }
    } finally {
      setLoadingCategories(false);
      loadingCategoriesRef.current = false;
    }
  };

  const loadMarketData = async () => {
    if (!actif || !actif.symbol) return;
    
    setLoading(true);
    const yfTicker = (actif.yfinance_symbol || actif.yfinanceSymbol || '').trim();
    
    try {
      let data = null;
      // 1) Si ticker yfinance est renseigné, récupérer les données réelles depuis yfinance (backend /macro/stock)
      if (yfTicker) {
        try {
          const yfResponse = await api.get(`/macro/stock/${encodeURIComponent(yfTicker)}`, { timeout: 15000 });
          if (yfResponse?.data) {
            data = {
              marketPrice: yfResponse.data.marketPrice ?? actif.marketPrice ?? 0,
              marketCap: yfResponse.data.marketCap ?? 0,
              avgVolume30d: yfResponse.data.avgVolume30d ?? 0,
              high52w: yfResponse.data.high52w ?? 0,
              low52w: yfResponse.data.low52w ?? 0,
              volatility30d: yfResponse.data.volatility30d ?? 0
            };
          }
        } catch (yfErr) {
          if (yfErr.response?.status !== 404 && process.env.NODE_ENV === 'development') {
            console.warn('[ActifDetail] yfinance fallback:', yfErr.response?.data?.detail || yfErr.message);
          }
        }
      }
      // 2) Sinon ou en complément : essayer IBKR (TWS) pour le prix marché
      if (!data || data.marketPrice === 0) {
        const symbol = actif.symbol;
        const secType = actif.secType || 'STK';
        const exchange = (actif.exchange && actif.exchange.trim() !== '') ? actif.exchange : 'SMART';
        const currency = (actif.currency && actif.currency.trim() !== '') ? actif.currency : 'USD';
        try {
          const response = await twsAPI.getMarketData(symbol, secType, exchange, currency);
          if (response?.data) {
            data = data ? { ...data, ...response.data } : response.data;
          }
        } catch (_) {
          // TWS non connecté ou erreur : garder data yfinance si on l'a
        }
      }
      if (data) {
        setMarketData(data);
      } else {
        setMarketData({
          marketPrice: actif.marketPrice || 0,
          marketCap: 0,
          avgVolume30d: 0,
          high52w: 0,
          low52w: 0,
          volatility30d: 0
        });
      }
    } catch (err) {
      console.error('Error loading market data:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur inconnue';
      if (err.response?.status === 503) {
        showToast('Connectez-vous à IB Gateway depuis Paramètres > API pour charger les données de marché.', 'warning', 3000, 'Connexion requise', 'Portefeuille', getPageIcon('Portefeuille'));
      } else if (err.response?.status !== 404) {
        showToast(errorMessage, 'error', 3000, 'Erreur de chargement', 'Portefeuille', getPageIcon('Portefeuille'));
      }
      setMarketData({
        marketPrice: actif.marketPrice || 0,
        marketCap: 0,
        avgVolume30d: 0,
        high52w: 0,
        low52w: 0,
        volatility30d: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (!actif) {
    return (
      <div className="actif-detail-page">
        <button onClick={onBack}>Retour</button>
      </div>
    );
  }

  // Utiliser les données de marché récupérées ou les valeurs par défaut
  const marketPrice = marketData?.marketPrice || actif.marketPrice || 0;
  const capitalization = marketData?.marketCap || 0;
  const avgVolume30d = marketData?.avgVolume30d || 0;
  const high52w = marketData?.high52w || 0;
  const low52w = marketData?.low52w || 0;
  const volatility30d = marketData?.volatility30d || 0;

  // Données de position (utiliser les mêmes données que le tableau du portefeuille)
  // Ces valeurs viennent directement d'IBKR et sont cohérentes avec le tableau
  const quantite = actif.position || actif.quantity || actif.quantite || 0;
  const prixAchat = actif.avgCost || actif.averageCost || actif.prix_moyen_achat || 0;
  const valeurAchat = actif.initialValue || (quantite * prixAchat); // Utiliser initialValue si disponible
  const valeurActuelle = actif.marketValue || (quantite * marketPrice); // Utiliser marketValue d'IBKR si disponible
  const gainPerte = actif.unrealizedPNL || (valeurActuelle - valeurAchat); // Utiliser unrealizedPNL d'IBKR si disponible
  const gainPertePct = actif.unrealizedPNLPercent || (valeurAchat > 0 ? (gainPerte / valeurAchat * 100) : 0);

  const formatCurrency = (value, currency = 'USD', decimals = 2) => {
    if (value === null || value === undefined || value === 0) return '-';
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
    return `${formatted}${symbol}`;
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === 0) return '-';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatPercentage = (value, decimals = 2) => {
    if (value === null || value === undefined || value === 0) return '-';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value) + '%';
  };

  const formatMarketCap = (value, currency = 'USD') => {
    if (value === null || value === undefined || value === 0) return '-';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
    if (value >= 1e12) {
      return `${(value / 1e12).toFixed(2).replace('.', ',')}T${symbol}`;
    } else if (value >= 1e9) {
      return `${(value / 1e9).toFixed(2).replace('.', ',')}B${symbol}`;
    } else if (value >= 1e6) {
      return `${(value / 1e6).toFixed(2).replace('.', ',')}M${symbol}`;
    }
    return formatCurrency(value, currency, 0);
  };

  const formatVolume = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    if (value >= 1e9) {
      return `${(value / 1e9).toFixed(2).replace('.', ',')} B`;
    } else if (value >= 1e6) {
      return `${(value / 1e6).toFixed(2).replace('.', ',')} M`;
    } else if (value >= 1e3) {
      return `${(value / 1e3).toFixed(2).replace('.', ',')} K`;
    }
    return formatNumber(value, 0);
  };

  const currency = actif.currency || 'USD';
  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;

  // Calcul du momentum (si on a high52w et low52w)
  let momentumPct = null;
  let momentumNiveau = 'N/A';
  let momentumSignal = 'N/A';
  if (high52w > 0 && low52w > 0 && marketPrice > 0) {
    momentumPct = ((marketPrice - low52w) / (high52w - low52w)) * 100;
    if (momentumPct >= 80) {
      momentumNiveau = 'Très Haut';
      momentumSignal = 'Suracheté';
    } else if (momentumPct >= 60) {
      momentumNiveau = 'Haut';
      momentumSignal = 'Fort';
    } else if (momentumPct >= 40) {
      momentumNiveau = 'Moyen';
      momentumSignal = 'Modéré';
    } else if (momentumPct >= 20) {
      momentumNiveau = 'Bas';
      momentumSignal = 'Faible';
    } else {
      momentumNiveau = 'Très Bas';
      momentumSignal = 'Survendu';
    }
  }

  // Signal de trading basique (à améliorer avec indicateurs techniques)
  let tradingSignal = 'Neutre';
  let tradingAction = 'Conserver';
  if (momentumPct !== null) {
    if (momentumPct < 20) {
      tradingSignal = 'Haussier';
      tradingAction = 'Acheter';
    } else if (momentumPct > 80) {
      tradingSignal = 'Baissier';
      tradingAction = 'Vendre';
    }
  }

  const handleEnrichFormChange = (field, value) => {
    setEnrichFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEnrichSubmit = async (e) => {
    e.preventDefault();
    if (!actif || !actif.symbol) {
      console.error('❌ Symbole de l\'actif manquant', { actif });
      showToast('Le symbole de l\'actif est manquant. Veuillez sélectionner un actif valide.', 'error', 3000, 'Erreur d\'enrichissement', 'Portefeuille', getPageIcon('Portefeuille'));
      return;
    }
    
    const symbol = actif.symbol;
    setLoadingEnrichment(true);
    
    try {
      // Trouver l'ID de la catégorie si un nom est fourni
      let categorieId = null;
      if (enrichFormData.categorie && enrichFormData.categorie.trim() !== '') {
        const selectedCategory = categories.find(c => c.name === enrichFormData.categorie);
        if (selectedCategory) {
          categorieId = selectedCategory.id;
        } else {
          console.warn('⚠️ Catégorie non trouvée:', enrichFormData.categorie, 'dans', categories.map(c => c.name));
        }
      }
      
      // Préparer les données à envoyer
      const enrichmentPayload = {
        yfinance_symbol: enrichFormData.yfinanceSymbol?.trim() || null,
        cours_cible: enrichFormData.coursCible && enrichFormData.coursCible.trim() !== '' 
          ? parseFloat(enrichFormData.coursCible) 
          : null,
        categorie_id: categorieId
      };
      // Utiliser updateBySymbol qui trouve ou crée l'actif automatiquement
      const response = await assetEnrichmentAPI.updateBySymbol(symbol, enrichmentPayload);
      setEnrichmentData(response.data);
      const changes = [];
      if (enrichFormData.yfinanceSymbol) changes.push(`Symbole YFinance: ${enrichFormData.yfinanceSymbol}`);
      if (enrichFormData.coursCible) changes.push(`Cours cible: ${enrichFormData.coursCible}`);
      if (enrichFormData.categorie) changes.push(`Catégorie: ${enrichFormData.categorie}`);
      const changesText = changes.length > 0 ? changes.join(', ') : 'Données mises à jour';
      showToast(changesText, 'success', 3000, 'Enrichissement d\'actif', 'Portefeuille', getPageIcon('Portefeuille'));
      setShowEnrichModal(false);
      
      // Émettre un événement pour notifier Portefeuille de recharger les données
      window.dispatchEvent(new CustomEvent('enrichmentUpdated', { 
        detail: { symbol: symbol } 
      }));
    } catch (err) {
      console.error('❌ Error saving enrichment data:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur inconnue';
      showToast(errorMessage, 'error', 3000, 'Erreur d\'enrichissement', 'Portefeuille', getPageIcon('Portefeuille'));
    } finally {
      setLoadingEnrichment(false);
    }
  };

  const handleCloseEnrichModal = () => {
    setShowEnrichModal(false);
    // Restaurer les valeurs originales si on annule
    setEnrichFormData({
      yfinanceSymbol: actif.yfinance_symbol || actif.yfinanceSymbol || '',
      coursCible: actif.cours_cible || actif.coursCible || '',
      categorie: actif.categorie || actif.category || ''
    });
  };

  const tabs = [
    {
      label: 'Synthèse',
      content: (
        <div className="actif-detail-tab-content">
          <div className="synthese-header">
            <h3>Synthèse de la position</h3>
            {canEdit && (
            <Tooltip title="Enrichir l'actif" placement="top">
              <span>
                <Button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowEnrichModal(true);
                  }}
                  variant="secondary"
                  type="button"
                >
                  Enrichir
                </Button>
              </span>
            </Tooltip>
            )}
          </div>
          
          {/* Métriques de position en 6 colonnes */}
          <div className="metrics-row">
            <Metric
              label="Position"
              value={formatNumber(Math.abs(quantite), 0)}
              help="Quantité détenue"
            />
            <Metric
              label="Valeur"
              value={formatCurrency(valeurActuelle, currency, 0)}
              help="Valeur actuelle de la position"
            />
            <Metric
              label="Prix d'achat"
              value={formatCurrency(prixAchat, currency, 2)}
              help="Prix moyen d'achat"
            />
            <Metric
              label="Gain/Perte"
              value={formatCurrency(gainPerte, currency, 2)}
              help={`Performance: ${gainPertePct >= 0 ? '+' : ''}${gainPertePct.toFixed(2)}%`}
            />
            <Metric
              label="Consensus"
              value="-"
              help="Prix cible consensus (à venir)"
            />
            <Metric
              label="P/E Ratio"
              value="-"
              help="Ratio cours/bénéfice (à venir)"
            />
          </div>

          {/* Analyse Momentum */}
          {momentumPct !== null && (
            <>
              <h3 style={{ marginTop: '2rem' }}>Analyse Momentum</h3>
              <div className="metrics-row">
                <Metric
                  label="Momentum %"
                  value={formatPercentage(momentumPct, 1)}
                  help="Position dans la fourchette 52 semaines"
                />
                <Metric
                  label="Niveau"
                  value={momentumNiveau}
                  help="Niveau de momentum"
                />
                <Metric
                  label="Signal"
                  value={momentumSignal}
                  help="Signal de momentum"
                />
              </div>
            </>
          )}

          {/* Signal de Trading */}
          <h3 style={{ marginTop: '2rem' }}>Signal de Trading</h3>
          <div className="metrics-row">
            <Metric
              label="Signal"
              value={tradingSignal}
              help="Signal de trading basé sur le momentum"
            />
            <Metric
              label="Action Recommandée"
              value={tradingAction}
              help="Action recommandée selon le signal"
            />
          </div>

          {/* Informations supplémentaires */}
          <div style={{ marginTop: '2rem' }}>
            <h3>Informations Détaillées</h3>
            <div className="info-grid">
              <div>
                <h4>Données Financières</h4>
                <ul>
                  <li>Capitalisation: {formatMarketCap(capitalization, currency)}</li>
                  <li>Volume moyen: {formatVolume(avgVolume30d)}</li>
                  <li>Catégorie: {actif.category || actif.categorie_principale || 'N/A'}</li>
                  <li>Ticker IBKR: {actif.symbol || 'N/A'}</li>
                </ul>
              </div>
              <div>
                <h4>Indicateurs Techniques</h4>
                <ul>
                  {volatility30d > 0 && (
                    <li>Volatilité (30j): {formatPercentage(volatility30d, 2)}</li>
                  )}
                  {momentumPct !== null && (
                    <>
                      <li>Momentum: {formatPercentage(momentumPct, 1)}</li>
                      {high52w > 0 && low52w > 0 && (
                        <>
                          <li>Distance du plus bas: {formatPercentage(((marketPrice - low52w) / low52w) * 100, 1)}</li>
                          <li>Distance du plus haut: {formatPercentage(((high52w - marketPrice) / marketPrice) * 100, 1)}</li>
                        </>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Note pour les données futures */}
          <div style={{ marginTop: '2rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
              <strong>Note:</strong> Les données de capitalisation, volume, volatilité et autres métriques avancées seront récupérées depuis une source externe dans une prochaine mise à jour. 
              Le prix de marché provient actuellement d'IBKR.
            </p>
          </div>
        </div>
      )
    },
    {
      label: 'Finance',
      content: (
        <div className="actif-detail-tab-content">
          <p>Données financières de {actif.symbol}</p>
          <p style={{ color: '#666', fontSize: '0.875rem' }}>
            Les données financières détaillées seront disponibles prochainement.
          </p>
        </div>
      )
    },
    {
      label: 'Performance',
      content: (
        <div className="actif-detail-tab-content">
          <p>Performance de {actif.symbol}</p>
          <p style={{ color: '#666', fontSize: '0.875rem' }}>
            L'analyse de performance sera disponible prochainement.
          </p>
        </div>
      )
    },
    {
      label: 'Sentiment',
      content: (
        <div className="actif-detail-tab-content">
          <p>Analyse du sentiment pour {actif.symbol}</p>
          <p style={{ color: '#666', fontSize: '0.875rem' }}>
            L'analyse de sentiment sera disponible prochainement.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="actif-detail-page">
      <div className="metrics-carousel-container">
        <div className="metrics-carousel">
        <div className="metric-card">
          <Metric
            label="Prix marché"
            value={formatCurrency(marketPrice, currency, 2)}
            help="Prix actuel du marché (IBKR)"
          />
        </div>
        <div className="metric-card">
          <Metric
            label="Capitalisation"
            value={formatMarketCap(capitalization, currency)}
            help="Capitalisation boursière"
          />
        </div>
        <div className="metric-card">
          <Metric
            label="Volume Moyen (30j)"
            value={formatVolume(avgVolume30d)}
            help="Volume moyen sur 30 jours"
          />
        </div>
        <div className="metric-card">
          <Metric
            label="Plus Haut 52S"
            value={formatCurrency(high52w, currency, 2)}
            help="Plus haut prix sur 52 semaines"
          />
        </div>
        <div className="metric-card">
          <Metric
            label="Plus Bas 52S"
            value={formatCurrency(low52w, currency, 2)}
            help="Plus bas prix sur 52 semaines"
          />
        </div>
        <div className="metric-card">
          <Metric
            label="Volatilité (30j)"
            value={formatPercentage(volatility30d, 2)}
            help="Volatilité sur 30 jours"
          />
        </div>
        </div>
      </div>
      <Tabs
        tabs={tabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />

      {/* Modal d'enrichissement */}
      <Modal
        isOpen={showEnrichModal}
        onClose={handleCloseEnrichModal}
        title="Enrichir l'actif"
        size="medium"
      >
        <div style={{ position: 'relative', width: '100%' }}>
          {/* Overlay de chargement avec dimensions fixes - toujours visible si chargement en cours */}
          {(loadingEnrichment || loadingCategories) && (
            <div style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1.5rem',
              textAlign: 'center',
              color: '#666',
              fontSize: '0.875rem',
              borderRadius: '8px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #333',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem'
              }}></div>
              <div style={{ fontWeight: 500 }}>Chargement des données...</div>
              {(loadingCategories && !loadingEnrichment) && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#999' }}>
                  Chargement des catégories...
                </div>
              )}
              {(loadingEnrichment && !loadingCategories) && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#999' }}>
                  Chargement des données d'enrichissement...
                </div>
              )}
            </div>
          )}
        <form onSubmit={handleEnrichSubmit} className="enrich-form" style={{ 
          opacity: (loadingEnrichment || loadingCategories) ? 0.3 : 1,
          pointerEvents: (loadingEnrichment || loadingCategories) ? 'none' : 'auto'
        }}>
          <div className="form-group">
            <label>Ticker yfinance *</label>
            <input
              type="text"
              value={enrichFormData.yfinanceSymbol}
              onChange={(e) => handleEnrichFormChange('yfinanceSymbol', e.target.value)}
              required
              placeholder="Ex: AAPL, MSFT, TSLA"
              disabled={loadingEnrichment}
            />
            <small>Symbole utilisé sur Yahoo Finance pour récupérer les données de marché</small>
          </div>

          <div className="form-group">
            <label>Cours cible</label>
            <input
              type="number"
              step="0.01"
              value={enrichFormData.coursCible}
              onChange={(e) => handleEnrichFormChange('coursCible', e.target.value)}
              placeholder="Ex: 150.00"
              disabled={loadingEnrichment}
            />
            <small>Prix cible pour cet actif (optionnel)</small>
          </div>

          <div className="form-group">
            <label>Catégorie</label>
            {(() => {
              const categoryOptions = categories.map(cat => ({
                value: cat.name,
                label: cat.name
              }));
              return (
                <Selectbox
                  value={enrichFormData.categorie || ''}
                  onChange={(value) => {
                    handleEnrichFormChange('categorie', value);
                  }}
                  placeholder={loadingCategories ? "Chargement des catégories..." : "Sélectionner une catégorie"}
                  options={categoryOptions}
                  disabled={loadingCategories || loadingEnrichment}
                />
              );
            })()}
            <small>Catégorie de l'actif (optionnel)</small>
          </div>

          {canEdit && (
          <div className="form-actions">
            <Button 
              type="submit" 
              variant="primary"
              disabled={loadingEnrichment}
            >
              {loadingEnrichment ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
          )}
        </form>
        </div>
      </Modal>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ActifDetail;
